import { Component, Inject, OnInit, Pipe, PipeTransform } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TlkModalWindowRef, TLK_MODAL_WINDOW_DATA, TlkModalWindowModule } from '@itero/ui-toolkit-angular/modal-window';
import { TlkButtonComponent } from '@itero/ui-toolkit-angular/button';
import { TlkBadgeComponent } from '@itero/ui-toolkit-angular/badge';
import { CloseEmptyIconComponent } from '@itero/ui-toolkit-angular/icons';
import { TranslateModule } from '@ngx-translate/core';
import { Patient, MatchCandidate } from '../../state-machine/patient-forms.types';
import { DobFormatPipe } from '../../services/dob-format.service';
import { PatientThumbnailService } from '../../services/patient-thumbnail.service';
import { PatientAvatarComponent } from '../patient-avatar/patient-avatar.component';

@Pipe({ name: 'genderInitial', standalone: true })
export class GenderInitialPipe implements PipeTransform {
	transform(gender: string): string {
		if (!gender) {
			return '—';
		}

		return gender.charAt(0).toUpperCase();
	}
}

export interface PatientLinkingData {
	sources: Patient[];
	candidates: MatchCandidate[];
	direction: 'iTero' | 'Invisalign';
	canCreateNew: boolean;
	/** When true, only the first candidate is shown pre-selected. No alternatives, no "different person". */
	confirmOnly?: boolean;
}

export type PatientLinkingResult =
	| { action: 'linked'; target: Patient }
	| { action: 'createNew' }
	| { action: 'skipInvisalign' }
	| { action: 'dismissed' }
	| undefined; // cancelled (back to search)

@Component({
	selector: 'rx-patient-linking-content',
	standalone: true,
	imports: [
		CommonModule,
		TlkModalWindowModule,
		TlkButtonComponent,
		TlkBadgeComponent,
		CloseEmptyIconComponent,
		TranslateModule,
		DobFormatPipe,
		GenderInitialPipe,
		PatientAvatarComponent
	],
	templateUrl: './patient-linking.component.html',
	styleUrls: ['./patient-linking.component.scss']
})
export class PatientLinkingContentComponent implements OnInit {
	selectedTarget: Patient | null = null;
	selectedCreateNew = false;
	thumbnailUrls = new Map<string, string>();

	constructor(
		public modalWindowRef: TlkModalWindowRef<PatientLinkingResult>,
		@Inject(TLK_MODAL_WINDOW_DATA) public data: PatientLinkingData,
		private readonly thumbnailService: PatientThumbnailService
	) {}

	ngOnInit(): void {
		if (this.data.confirmOnly && this.data.candidates.length > 0) {
			this.selectedTarget = this.data.candidates[0].patient;
		}

		const ids = [...this.data.sources, ...this.data.candidates.map(c => c.patient)]
			.filter((p): p is Patient & { invisalignId: string } => !!p.invisalignId)
			.map(p => p.invisalignId);

		if (ids.length) {
			this.thumbnailService.getThumbnails(ids).subscribe(urls => {
				this.thumbnailUrls = urls;
			});
		}
	}

	get sourcePatient(): Patient {
		return this.data.sources[0];
	}

	get targetSystem(): 'iTero' | 'Invisalign' {
		return this.data.direction === 'iTero' ? 'Invisalign' : 'iTero';
	}

	get sourceDescription(): string {
		if (this.data.confirmOnly) {
			return this.data.direction === 'iTero' ? 'PatientLinking.ConfirmYourITero' : 'PatientLinking.ConfirmYourInvisalign';
		}

		return this.data.direction === 'iTero' ? 'PatientLinking.YourSelectedITero' : 'PatientLinking.YourSelectedInvisalign';
	}

	get candidatesDescription(): string {
		if (this.data.confirmOnly) {
			return this.data.direction === 'iTero' ? 'PatientLinking.ConfirmLinkToInvisalign' : 'PatientLinking.ConfirmLinkToITero';
		}

		return this.data.direction === 'iTero' ? 'PatientLinking.FoundOnInvisalign' : 'PatientLinking.FoundOnITero';
	}

	get canLink(): boolean {
		return this.selectedTarget !== null || this.selectedCreateNew;
	}

	get confirmLabel(): string {
		return this.selectedCreateNew ? 'PatientLinking.ContinueWithoutLinking' : 'PatientLinking.LinkPatients';
	}

	selectTarget(candidate: MatchCandidate): void {
		if (candidate.wouldCreateDuplicate) {
			return;
		}

		this.selectedCreateNew = false;
		this.selectedTarget = candidate.patient;
	}

	selectCreateNew(): void {
		this.selectedTarget = null;
		this.selectedCreateNew = true;
	}

	confirmLink(): void {
		if (this.selectedCreateNew) {
			this.modalWindowRef.close({ action: 'createNew' });
		} else if (this.selectedTarget) {
			this.modalWindowRef.close({ action: 'linked', target: this.selectedTarget });
		}
	}

	skipInvisalign(): void {
		this.modalWindowRef.close({ action: 'skipInvisalign' });
	}

	close(): void {
		this.modalWindowRef.close(this.data.confirmOnly ? { action: 'dismissed' } : undefined);
	}
}
